import React from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import TimelineData from "../data/TimelineData.json";
import { useScrollAnimation } from "../hooks/useScrollAnimation";
import SectionHeading from "../components/SectionHeading";

function TimelineCard({ item, color }) {
  return (
    <Card
      sx={{
        width: "100%",
        maxWidth: "450px",
        background: `linear-gradient(135deg, ${color}12, ${color}28)`,
        border: `1px solid ${color}35`,
        backdropFilter: "blur(10px)",
      }}
    >
      <CardContent sx={{ p: { xs: 2, md: 2.5 } }}>
        <Typography
          variant="caption"
          sx={{
            color: color,
            opacity: 0.7,
            mb: 0.5,
            display: "block",
            fontFamily: "monospace",
            fontSize: "0.75rem",
          }}
        >
          {item.title}
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: "text.primary",
            mb: 0.5,
            fontWeight: 600,
            fontSize: { xs: "0.95rem", md: "1.05rem" },
          }}
        >
          {item.cardTitle}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.5, fontSize: "0.85rem" }}>
          {item.cardSubtitle}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", fontSize: "0.83rem", opacity: 0.8 }}>
          {item.cardDetailedText}
        </Typography>
      </CardContent>
    </Card>
  );
}

function TimelineItem({ item, index, color }) {
  const isLeft = index % 2 === 0;

  return (
    <>
      {/* Mobile: single-column */}
      <Box
        sx={{
          display: { xs: "flex", md: "none" },
          mb: 3,
          gap: 2,
          alignItems: "flex-start",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0, pt: 0.5 }}>
          <Box
            sx={{
              width: 12,
              height: 12,
              borderRadius: "50%",
              bgcolor: color,
              border: "3px solid #09090b",
              boxShadow: `0 0 0 3px ${color}30`,
            }}
          />
          <Box sx={{ width: 2, flex: 1, minHeight: 40, bgcolor: `${color}30`, mt: 1 }} />
        </Box>
        <TimelineCard item={item} color={color} />
      </Box>

      {/* Desktop: alternating */}
      <Box
        sx={{
          display: { xs: "none", md: "flex" },
          justifyContent: "center",
          alignItems: "center",
          position: "relative",
          mb: 4,
        }}
      >
        <Box sx={{ width: "45%", display: "flex", justifyContent: "flex-end", pr: 3 }}>
          {isLeft && <TimelineCard item={item} color={color} />}
        </Box>
        <Box
          sx={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            backgroundColor: color,
            border: "3px solid #09090b",
            zIndex: 2,
            flexShrink: 0,
            boxShadow: `0 0 0 4px ${color}25, 0 0 16px ${color}40`,
          }}
        />
        <Box sx={{ width: "45%", display: "flex", justifyContent: "flex-start", pl: 3 }}>
          {!isLeft && <TimelineCard item={item} color={color} />}
        </Box>
      </Box>
    </>
  );
}

function TimelineSection({ items, color, title }) {
  return (
    <Box sx={{ mb: { xs: 6, md: 8 } }}>
      <Typography
        variant="h4"
        sx={{
          mb: { xs: 3, md: 4 },
          fontSize: { xs: "1.3rem", sm: "1.6rem", md: "1.8rem" },
          color,
          fontWeight: 600,
          textAlign: "center",
          textShadow: `0 0 30px ${color}50`,
        }}
      >
        {title}
      </Typography>

      {/* Desktop vertical line */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            left: "50%",
            transform: "translateX(-50%)",
            width: "1px",
            height: "100%",
            background: `linear-gradient(to bottom, ${color}80, ${color}20)`,
          },
        }}
      >
        {items.map((item, index) => (
          <TimelineItem key={index} item={item} index={index} color={color} />
        ))}
      </Box>

      {/* Mobile list */}
      <Box sx={{ display: { xs: "block", md: "none" }, pl: 1 }}>
        {items.map((item, index) => (
          <TimelineItem key={index} item={item} index={index} color={color} />
        ))}
      </Box>
    </Box>
  );
}

function WorkTimelineContainer() {
  const { t, i18n } = useTranslation();
  const { ref, visible } = useScrollAnimation();
  const currentLang = i18n.language || "en";

  const schoolItems = TimelineData[currentLang]?.education || TimelineData.en.education;
  const workItems = TimelineData[currentLang]?.work || TimelineData.en.work;

  return (
    <Box
      id="journey"
      ref={ref}
      sx={{
        py: { xs: 4, md: 8 },
        px: { xs: 1, sm: 2 },
        scrollMarginTop: { xs: "56px", md: "64px" },
        opacity: visible ? 1 : 0,
        transform: visible ? "none" : "translateY(40px)",
        transition: "opacity 0.7s ease, transform 0.7s ease",
      }}
    >
      <SectionHeading tag="journey" title={t("timeline.title")} number={4} />
      <TimelineSection
        items={schoolItems}
        color="#818cf8"
        title={t("timeline.educationTitle")}
      />
      <TimelineSection
        items={workItems}
        color="#22d3ee"
        title={t("timeline.workTitle")}
      />
    </Box>
  );
}

export default WorkTimelineContainer;
